/**
 * Shared client-side validation for task questions authored in AdminTasks.
 *
 * Strict save rules (matches Phase A backend contract in
 * `backend/services/questions.py::validate_and_normalize_questions`):
 *   - text must be non-empty
 *   - option-based types must have >= 2 non-empty options
 *   - conditional_single_select.conditional_option must be one of options
 *   - multi_select_card.min_selections <= max_selections
 *   - matrix_agree_disagree must have >= 1 statement
 *
 * Ground truth is NEVER required — questions with empty ground_truth are
 * treated by the grader as subjective (weight=0). The builder flags them
 * with a warning icon so admins choose intentionally.
 */

export const NEEDS_OPTIONS = new Set([
  "single_select_card", "multi_select_card", "dropdown", "conditional_single_select",
]);

/** Returns a human-readable error string, or null when the question is valid. */
export function validateQuestion(q, idx) {
  const label = `Question ${idx + 1}`;
  if (!q || typeof q !== "object") return `${label}: invalid`;
  if (!q.text || !q.text.trim()) return `${label}: text is required`;

  if (NEEDS_OPTIONS.has(q.type)) {
    const opts = (q.options || []).map((o) => (o || "").trim()).filter(Boolean);
    if (opts.length < 2) return `${label}: needs at least 2 non-empty options`;
    if (q.type === "conditional_single_select") {
      const cond = (q.conditional_option || "").trim();
      if (cond && !opts.includes(cond)) {
        return `${label}: follow-up trigger must be one of the options`;
      }
    }
  }
  if (q.type === "multi_select_card") {
    const min = Number(q.min_selections || 0);
    const max = Number(q.max_selections || 1);
    if (min > max) return `${label}: min selections cannot exceed max selections`;
  }
  if (q.type === "matrix_agree_disagree") {
    const stmts = (q.matrix_statements || []).map((s) => (s || "").trim()).filter(Boolean);
    if (stmts.length < 1) return `${label}: needs at least 1 statement`;
  }
  return null;
}

/** Validate whole array. Returns { ok, errors[], warnings[] }. */
export function validateQuestions(questions) {
  const errors = [];
  const warnings = [];
  (questions || []).forEach((q, i) => {
    const err = validateQuestion(q, i);
    if (err) errors.push(err);
    if (!hasGroundTruth(q)) {
      warnings.push(`Question ${i + 1}: no ground truth — won't be graded automatically.`);
    }
  });
  return { ok: errors.length === 0, errors, warnings };
}

/** True when the question has *some* ground truth entered for its type. */
export function hasGroundTruth(q) {
  const gt = q && q.ground_truth;
  if (!gt || typeof gt !== "object") return false;
  const t = q.type;
  if (t === "single_select_card" || t === "dropdown") {
    return !!(gt.correct_option || (gt.correct_options && gt.correct_options.length));
  }
  if (t === "conditional_single_select") {
    return !!(gt.correct_option || (gt.correct_options && gt.correct_options.length) ||
      (gt.text_keywords && gt.text_keywords.length));
  }
  if (t === "multi_select_card") {
    return !!(gt.correct_set && gt.correct_set.length);
  }
  if (t === "short_text" || t === "long_text") {
    return !!((gt.reference_answer && gt.reference_answer.trim()) ||
      (gt.keywords && gt.keywords.length));
  }
  if (t === "likert_5") {
    return typeof gt.expected === "number" && gt.expected >= 1;
  }
  if (t === "matrix_agree_disagree") {
    return !!(gt.expected && Object.keys(gt.expected).length);
  }
  return false;
}
